import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Button, IconButton } from '@ui/components/Button'
import { MobileIcon } from '@ui/icons/Mobile.icon'
import { TrashIcon } from '@ui/icons/Trash.icon'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@ui/v2/Table/Table'

import { ClientPhoneNumberDto } from '@model/AccountDetailedDto'

import { Box } from '@shared/components/Box'

import { PhoneNumberStatus } from './PhoneNumberStatus'

type PhoneNumbersTableProps = {
  phoneNumbers: ClientPhoneNumberDto[]
  isLoading: boolean
  deleteNumber(number: ClientPhoneNumberDto): void
  verifyNumber(number: ClientPhoneNumberDto): void
  makeNumberPrimary(number: ClientPhoneNumberDto): void
}

export const PhoneNumbersTable: React.FC<PhoneNumbersTableProps> = (props) => {
  const { phoneNumbers, isLoading, verifyNumber, deleteNumber, makeNumberPrimary } = props
  const { t } = useTranslation()

  const primaryNumbers = useMemo(() => phoneNumbers.filter((p) => p.isPrimary), [phoneNumbers])
  const secondaryNumbers = useMemo(() => phoneNumbers.filter((p) => !p.isPrimary), [phoneNumbers])

  const renderSection = (title: string, rows: ClientPhoneNumberDto[], isPrimarySection = false) => (
    <>
      <TableHeader>
        <TableRow>
          <TableHead colSpan={5}>{title}</TableHead>
        </TableRow>
      </TableHeader>

      <TableBody>
        {rows.map((phone) => {
          const canMakePrimary = !isPrimarySection && !phone.isUsedIn2FA
          const canDelete = !isPrimarySection && !phone.isUsedIn2FA

          return (
            <TableRow key={phone.id}>
              <TableCell>
                <SensitiveData>
                  {phone.countryCode} {phone.number}
                </SensitiveData>
              </TableCell>

              <TableCell>
                <PhoneNumberStatus
                  shouldVerify={phone.shouldVerify}
                  isVerified={phone.isVerified}
                  showIcon
                  verify={() => verifyNumber(phone)}
                />
              </TableCell>

              <TableCell>{phone.isUsedIn2FA ? t('PhoneNumbers.2FA') : null}</TableCell>

              <TableCell>
                {canMakePrimary && (
                  <Box flex align='center' gap='x8'>
                    <Button.Ghost
                      iconLeft={MobileIcon}
                      onClick={(event) => {
                        if (isLoading) {
                          event.preventDefault()
                        } else {
                          makeNumberPrimary(phone)
                        }
                      }}
                    >
                      {t('PhoneNumbers.Set as primary')}
                    </Button.Ghost>
                  </Box>
                )}
              </TableCell>

              <TableCell>
                {canDelete && (
                  <IconButton.Ghost
                    icon={TrashIcon}
                    disabled={isLoading}
                    onClick={() => !isLoading && deleteNumber(phone)}
                  />
                )}
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </>
  )

  return (
    <Table noResults={phoneNumbers.length === 0}>
      {renderSection(t('PhoneNumbers.Primary phone'), primaryNumbers, true)}
      {renderSection(t('PhoneNumbers.Secondary phone'), secondaryNumbers, false)}
    </Table>
  )
}
